import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, X, ChevronLeft, ChevronRight, Check, Truck, ShoppingBag, Users, Search } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { config } from '../../config/environment';
import { useBodyLock } from '../../hooks/useBodyLock';

interface Categoria {
  id: number;
  nome: string;
  ordem: number;
  ativo: boolean;
  imagem_url?: string;
  visivel_delivery: boolean;
  visivel_garcom: boolean;
  visivel_pdv: boolean;
  precisa_cozinha: boolean;
  perguntar_imprimir: boolean;
  sempre_imprimir: boolean;
  impressora_destino?: string;
}

interface CategoriaForm {
  nome: string;
  ordem: number;
  imagem?: File;
  visivel_delivery: boolean;
  visivel_garcom: boolean;
  visivel_pdv: boolean;
  precisa_cozinha: boolean;
  perguntar_imprimir: boolean;
  sempre_imprimir: boolean;
  impressora_destino?: string;
}

const CategoriasPage: React.FC = () => {
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState<Categoria | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [filtroVisibilidade, setFiltroVisibilidade] = useState<{
    delivery: boolean | null;
    pdv: boolean | null;
    garcom: boolean | null;
  }>({
    delivery: null,
    pdv: null,
    garcom: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const totalSteps = 2;

  // Bloquear scroll do body quando modal aberto no mobile
  useBodyLock(showModal);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors }
  } = useForm<CategoriaForm>({
    defaultValues: {
      visivel_delivery: true,
      visivel_garcom: true,
      visivel_pdv: true,
      precisa_cozinha: true,
      perguntar_imprimir: false,
      sempre_imprimir: false,
      impressora_destino: ''
    }
  });

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    try {
      const response = await api.get('/api/categorias');
      setCategorias(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tamanho do arquivo (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
        return;
      }

      // Validar tipo do arquivo
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione apenas arquivos de imagem');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Validar passo atual
  const validateStep = (step: number, data: CategoriaForm): boolean => {
    switch (step) {
      case 1:
        if (!data.nome?.trim() || data.ordem === undefined) {
          toast.error('Preencha todos os campos obrigatórios do passo 1');
          return false;
        }
        if (data.ordem < 0) {
          toast.error('A ordem deve ser maior ou igual a zero');
          return false;
        }
        return true;
      case 2:
        // Passo 2 (imagem) é opcional
        return true;
      default:
        return false;
    }
  };

  // Navegar para próximo passo
  const handleNextStep = (data: CategoriaForm) => {
    if (validateStep(currentStep, data)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        // Último passo - salvar categoria
        handleFinalSubmit(data);
      }
    }
  };

  // Voltar passo anterior
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Salvar categoria final
  const handleFinalSubmit = async (data: CategoriaForm) => {
    try {
      console.log('=== DEBUG: Iniciando envio de categoria ===');
      console.log('Dados do formulário:', data);
      console.log('Imagem selecionada:', selectedImage);
      console.log('Editando categoria:', editingCategoria);

      const formData = new FormData();
      formData.append('nome', data.nome);
      formData.append('ordem', data.ordem.toString());
      formData.append('visivel_delivery', data.visivel_delivery ? '1' : '0');
      formData.append('visivel_garcom', data.visivel_garcom ? '1' : '0');
      formData.append('visivel_pdv', data.visivel_pdv ? '1' : '0');
      formData.append('precisa_cozinha', '1'); // Forçar como ativo
      formData.append('perguntar_imprimir', data.perguntar_imprimir ? '1' : '0');
      formData.append('sempre_imprimir', data.sempre_imprimir ? '1' : '0');
      if (data.impressora_destino) formData.append('impressora_destino', data.impressora_destino);

      // Se estiver editando, incluir o campo ativo
      if (editingCategoria) {
        formData.append('ativo', editingCategoria.ativo.toString());
        console.log('Adicionando campo ativo:', editingCategoria.ativo);
      }

      if (selectedImage) {
        console.log('Adicionando imagem ao FormData:', selectedImage.name, selectedImage.size, selectedImage.type);
        formData.append('imagem', selectedImage);
      }

      // Log do FormData para debug
      for (let [key, value] of formData.entries()) {
        console.log('FormData entry:', key, value);
      }

      if (editingCategoria) {
        console.log('Atualizando categoria ID:', editingCategoria.id);
        const response = await api.put(`/api/categorias/${editingCategoria.id}`, formData);
        console.log('Resposta da atualização:', response.data);
        toast.success('Categoria atualizada com sucesso!');
      } else {
        console.log('Criando nova categoria');
        const response = await api.post('/api/categorias', formData);
        console.log('Resposta da criação:', response.data);
        toast.success('Categoria criada com sucesso!');
      }

      setShowModal(false);
      setEditingCategoria(null);
      setSelectedImage(null);
      setImagePreview(null);
      setCurrentStep(1);
      reset();
      fetchCategorias();
    } catch (error: any) {
      console.error('=== ERRO AO SALVAR CATEGORIA ===');
      console.error('Erro completo:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      console.error('Response headers:', error.response?.headers);

      // Tratamento específico para diferentes tipos de erro
      let errorMessage = 'Erro ao salvar categoria';

      if (error.response) {
        // Erro do servidor
        if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.status === 413) {
          errorMessage = 'Arquivo muito grande. Tamanho máximo: 5MB';
        } else if (error.response.status === 400) {
          errorMessage = 'Dados inválidos. Verifique as informações';
        } else if (error.response.status === 401) {
          errorMessage = 'Sessão expirada. Faça login novamente';
        } else if (error.response.status === 500) {
          errorMessage = 'Erro interno do servidor';
        }
      } else if (error.request) {
        // Erro de rede
        errorMessage = 'Erro de conexão. Verifique sua internet';
      } else {
        // Outros erros
        errorMessage = error.message || 'Erro desconhecido';
      }

      toast.error(errorMessage);
    }
  };

  const onSubmit = (data: CategoriaForm) => {
    handleNextStep(data);
  };

  const handleEdit = (categoria: Categoria) => {
    setEditingCategoria(categoria);
    setCurrentStep(1);

    // Configurar preview da imagem se existir
    if (categoria.imagem_url) {
      // Verificar se a URL é relativa ou absoluta
      const imageUrl = categoria.imagem_url.startsWith('http')
        ? categoria.imagem_url
        : `${config.api.baseUrl}/${categoria.imagem_url}`;
      setImagePreview(imageUrl);
    } else {
      setImagePreview(null);
    }

    reset({
      nome: categoria.nome,
      ordem: categoria.ordem,
      visivel_delivery: categoria.visivel_delivery !== undefined ? categoria.visivel_delivery : true,
      visivel_garcom: categoria.visivel_garcom !== undefined ? categoria.visivel_garcom : true,
      visivel_pdv: categoria.visivel_pdv !== undefined ? categoria.visivel_pdv : true,
      precisa_cozinha: true, // Sempre enviar como ativo
      perguntar_imprimir: categoria.perguntar_imprimir !== undefined ? Boolean(categoria.perguntar_imprimir) : false,
      sempre_imprimir: categoria.sempre_imprimir !== undefined ? Boolean(categoria.sempre_imprimir) : false,
      impressora_destino: categoria.impressora_destino || ''
    });
    setShowModal(true);
  };

  const handleToggleStatus = async (categoria: Categoria) => {
    try {
      const formData = new FormData();
      formData.append('nome', categoria.nome);
      formData.append('ordem', categoria.ordem.toString());
      formData.append('ativo', (!categoria.ativo).toString());

      // Não enviar imagem_url aqui, apenas o status
      // A imagem será mantida pelo backend

      await api.put(`/api/categorias/${categoria.id}`, formData);
      toast.success(`Categoria ${!categoria.ativo ? 'ativada' : 'desativada'} com sucesso!`);
      fetchCategorias();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Erro ao alterar status da categoria';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        await api.delete(`/api/categorias/${id}`);
        toast.success('Categoria excluída com sucesso!');
        fetchCategorias();
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || 'Erro ao excluir categoria';
        toast.error(errorMessage);
      }
    }
  };

  // Filtrar categorias por visibilidade
  const categoriasFiltradas = categorias.filter(categoria => {
    // Filtros por visibilidade
    if (filtroVisibilidade.delivery !== null && categoria.visivel_delivery !== filtroVisibilidade.delivery) {
      return false;
    }
    if (filtroVisibilidade.pdv !== null && categoria.visivel_pdv !== filtroVisibilidade.pdv) {
      return false;
    }
    if (filtroVisibilidade.garcom !== null && categoria.visivel_garcom !== filtroVisibilidade.garcom) {
      return false;
    }

    // Filtro por termo de busca (nome)
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      return categoria.nome.toLowerCase().includes(term);
    }

    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header - Mobile: Stack, iPad/Desktop: Row */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-4">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
            Categorias
          </h1>
          <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
            Gerencie as categorias do seu menu
          </p>
        </div>
        <button
          onClick={() => {
            setShowModal(true);
            setEditingCategoria(null);
            setSelectedImage(null);
            setImagePreview(null);
            setCurrentStep(1);
            reset({
              visivel_delivery: true,
              visivel_garcom: true,
              visivel_pdv: true
            });
          }}
          className="btn-primary flex items-center justify-center gap-2 w-full md:w-auto text-sm md:text-base px-4 py-2 md:py-2.5"
        >
          <Plus className="h-4 w-4 md:h-5 md:w-5" />
          <span className="hidden md:inline">Nova Categoria</span>
          <span className="md:hidden">Nova</span>
        </button>
      </div>

      {/* Barra de Pesquisa e Filtros */}
      <div className="space-y-3">
        {/* Barra de Pesquisa */}
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
          </div>
          <input
            type="text"
            placeholder="Pesquisar categoria pelo nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filtros de Visibilidade */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 p-3 md:p-4 lg:p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-2 w-full md:w-auto">
          <label className="text-xs md:text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
            Visibilidade:
          </label>
          <div className="flex flex-wrap gap-2">
            {/* Filtro Delivery */}
            <button
              onClick={() => setFiltroVisibilidade(prev => ({
                ...prev,
                delivery: prev.delivery === null ? true : prev.delivery === true ? false : null
              }))}
              className={`px-3 py-1.5 text-xs md:text-sm rounded-full border transition-colors ${filtroVisibilidade.delivery === true
                ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                : filtroVisibilidade.delivery === false
                  ? 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                }`}
              title={filtroVisibilidade.delivery === true ? 'Mostrando Delivery' : filtroVisibilidade.delivery === false ? 'Ocultando Delivery' : 'Todos (Delivery)'}
            >
              <Truck className="h-3 w-3 inline mr-1" />
              Delivery
            </button>

            {/* Filtro PDV */}
            <button
              onClick={() => setFiltroVisibilidade(prev => ({
                ...prev,
                pdv: prev.pdv === null ? true : prev.pdv === true ? false : null
              }))}
              className={`px-3 py-1.5 text-xs md:text-sm rounded-full border transition-colors ${filtroVisibilidade.pdv === true
                ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                : filtroVisibilidade.pdv === false
                  ? 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                }`}
              title={filtroVisibilidade.pdv === true ? 'Mostrando PDV' : filtroVisibilidade.pdv === false ? 'Ocultando PDV' : 'Todos (PDV)'}
            >
              <ShoppingBag className="h-3 w-3 inline mr-1" />
              PDV
            </button>

            {/* Filtro Garçom */}
            <button
              onClick={() => setFiltroVisibilidade(prev => ({
                ...prev,
                garcom: prev.garcom === null ? true : prev.garcom === true ? false : null
              }))}
              className={`px-3 py-1.5 text-xs md:text-sm rounded-full border transition-colors ${filtroVisibilidade.garcom === true
                ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300'
                : filtroVisibilidade.garcom === false
                  ? 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                }`}
              title={filtroVisibilidade.garcom === true ? 'Mostrando Garçom' : filtroVisibilidade.garcom === false ? 'Ocultando Garçom' : 'Todos (Garçom)'}
            >
              <Users className="h-3 w-3 inline mr-1" />
              Garçom
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between md:justify-start gap-3 md:gap-4 ml-auto">
          {(filtroVisibilidade.delivery !== null || filtroVisibilidade.pdv !== null || filtroVisibilidade.garcom !== null) && (
            <button
              onClick={() => {
                setFiltroVisibilidade({
                  delivery: null,
                  pdv: null,
                  garcom: null
                });
              }}
              className="text-xs md:text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 whitespace-nowrap"
            >
              Limpar filtros
            </button>
          )}

          <div className="text-xs md:text-sm lg:text-base text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {categoriasFiltradas.length} de {categorias.length} categorias
          </div>
        </div>
      </div>
    </div>

      {/* Categorias Grid - Mobile: 1 col, iPad: 2 cols, Desktop: 3 cols */}
      {categoriasFiltradas.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
          {categoriasFiltradas.map((categoria) => (
            <div
              key={categoria.id}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border-2 transition-transform hover:scale-[1.02] md:hover:scale-[1.01] ${categoria.ativo ? 'border-green-200 dark:border-green-700' : 'border-red-200 dark:border-red-700'}`}
            >
              {/* Imagem de fundo da categoria */}
              <div className="relative h-32 md:h-36 lg:h-40 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
                {categoria.imagem_url ? (
                  <div className="relative w-full h-full">
                    <img
                      src={categoria.imagem_url.startsWith('http')
                        ? categoria.imagem_url
                        : `${config.api.baseUrl}/${categoria.imagem_url}`}
                      alt={categoria.nome}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <h3 className="text-white text-base md:text-lg lg:text-xl font-bold text-center px-4">
                        {categoria.nome}
                      </h3>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <h3 className="text-gray-600 dark:text-gray-300 text-base md:text-lg lg:text-xl font-bold text-center px-4">
                      {categoria.nome}
                    </h3>
                  </div>
                )}
              </div>

              <div className="p-3 md:p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Ordem: {categoria.ordem}
                  </span>
                  <span className={`px-2 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium whitespace-nowrap ${categoria.ativo
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                    {categoria.ativo ? 'Ativa' : 'Inativa'}
                  </span>
                </div>

                {/* Ícones de Visualização */}
                <div className="mb-3 flex items-center gap-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Visível em:</span>
                  <div className="flex gap-1">
                    {categoria.visivel_delivery ? (
                      <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded" title="Delivery">
                        <Truck className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                      </div>
                    ) : (
                      <div className="p-1 bg-gray-100 dark:bg-gray-700 rounded opacity-30" title="Delivery - Invisível">
                        <Truck className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}

                    {categoria.visivel_pdv ? (
                      <div className="p-1 bg-green-100 dark:bg-green-900/30 rounded" title="PDV">
                        <ShoppingBag className="h-3 w-3 text-green-600 dark:text-green-400" />
                      </div>
                    ) : (
                      <div className="p-1 bg-gray-100 dark:bg-gray-700 rounded opacity-30" title="PDV - Invisível">
                        <ShoppingBag className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}

                    {categoria.visivel_garcom ? (
                      <div className="p-1 bg-orange-100 dark:bg-orange-900/30 rounded" title="Garçom">
                        <Users className="h-3 w-3 text-orange-600 dark:text-orange-400" />
                      </div>
                    ) : (
                      <div className="p-1 bg-gray-100 dark:bg-gray-700 rounded opacity-30" title="Garçom - Invisível">
                        <Users className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-1 md:gap-2">
                  <button
                    onClick={() => handleEdit(categoria)}
                    className="flex-1 bg-blue-500 text-white px-2 md:px-3 py-1.5 md:py-2 rounded text-xs md:text-sm hover:bg-blue-600 transition-colors flex items-center justify-center gap-1"
                    title="Editar"
                  >
                    <Edit className="h-3 w-3 md:h-3.5 md:w-3.5" />
                    <span className="hidden md:inline">Editar</span>
                  </button>
                  <button
                    onClick={() => handleToggleStatus(categoria)}
                    className="px-2 md:px-3 py-1.5 md:py-2 rounded text-xs md:text-sm transition-colors flex items-center justify-center gap-1"
                    style={{
                      backgroundColor: categoria.ativo ? '#fef3c7' : '#dcfce7',
                      color: categoria.ativo ? '#92400e' : '#166534'
                    }}
                    title={categoria.ativo ? 'Desativar' : 'Ativar'}
                  >
                    {categoria.ativo ? <EyeOff className="h-3 w-3 md:h-3.5 md:w-3.5" /> : <Eye className="h-3 w-3 md:h-3.5 md:w-3.5" />}
                    <span className="hidden md:inline">{categoria.ativo ? 'Desativar' : 'Ativar'}</span>
                  </button>
                  <button
                    onClick={() => handleDelete(categoria.id)}
                    className="px-2 md:px-3 py-1.5 md:py-2 bg-red-500 text-white rounded text-xs md:text-sm hover:bg-red-600 transition-colors flex items-center justify-center gap-1"
                    title="Excluir"
                  >
                    <Trash2 className="h-3 w-3 md:h-3.5 md:w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 md:py-12 lg:py-16">
          <div className="text-gray-500 dark:text-gray-400 mb-4 md:mb-6">
            <p className="text-base md:text-lg lg:text-xl font-medium mb-2">
              {filtroVisibilidade.delivery !== null || filtroVisibilidade.pdv !== null || filtroVisibilidade.garcom !== null
                ? 'Nenhuma categoria encontrada com estes filtros'
                : 'Nenhuma categoria cadastrada'}
            </p>
            <p className="text-xs md:text-sm lg:text-base">
              {filtroVisibilidade.delivery !== null || filtroVisibilidade.pdv !== null || filtroVisibilidade.garcom !== null
                ? 'Tente ajustar seus filtros de visualização.'
                : 'Comece criando sua primeira categoria!'}
            </p>
          </div>
          <button
            onClick={() => {
              setShowModal(true);
              setEditingCategoria(null);
              setSelectedImage(null);
              setImagePreview(null);
              setCurrentStep(1);
              reset({
                visivel_delivery: true,
                visivel_garcom: true,
                visivel_pdv: true,
                precisa_cozinha: true,
                perguntar_imprimir: false,
                sempre_imprimir: false
              });
            }}
            className="btn-primary flex items-center gap-2 mx-auto text-sm md:text-base px-4 py-2 md:py-2.5"
          >
            <Plus className="h-4 w-4 md:h-5 md:w-5" />
            Criar Primeira Categoria
          </button>
        </div>
      )}

      {/* Modal de Categoria - Passo a Passo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md md:max-w-lg lg:max-w-2xl max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-5 lg:p-6">
              {/* Header com título e botão fechar */}
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                  {editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategoria(null);
                    setSelectedImage(null);
                    setImagePreview(null);
                    setCurrentStep(1);
                    reset();
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
                >
                  <X className="h-5 w-5 md:h-6 md:w-6" />
                </button>
              </div>

              {/* Indicador de Progresso */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-center justify-between mb-2">
                  {[1, 2].map((step) => (
                    <React.Fragment key={step}>
                      <div className="flex items-center">
                        <div
                          className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full border-2 transition-colors ${currentStep >= step
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-400'
                            }`}
                        >
                          {currentStep > step ? (
                            <Check className="h-4 w-4 md:h-5 md:w-5" />
                          ) : (
                            <span className="text-xs md:text-sm font-semibold">{step}</span>
                          )}
                        </div>
                        <span
                          className={`ml-2 text-xs md:text-sm font-medium hidden md:block ${currentStep >= step
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-400'
                            }`}
                        >
                          {step === 1 && 'Informações Básicas'}
                          {step === 2 && 'Imagem'}
                        </span>
                      </div>
                      {step < totalSteps && (
                        <div
                          className={`flex-1 h-0.5 mx-2 md:mx-4 ${currentStep > step ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="text-center mt-2">
                  <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Passo {currentStep} de {totalSteps}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 md:space-y-4">
                {/* Passo 1: Informações Básicas */}
                {currentStep === 1 && (
                  <div className="space-y-3 md:space-y-4">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nome da Categoria <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register('nome', { required: 'Nome é obrigatório' })}
                        className="input-field text-sm md:text-base"
                        placeholder="Digite o nome da categoria"
                      />
                      {errors.nome && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">{errors.nome.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Ordem <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        {...register('ordem', {
                          required: 'Ordem é obrigatória',
                          min: { value: 0, message: 'Ordem deve ser maior ou igual a 0' }
                        })}
                        className="input-field text-sm md:text-base"
                        placeholder="0"
                      />
                      {errors.ordem && (
                        <p className="mt-1 text-xs md:text-sm text-red-600">{errors.ordem.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        A ordem determina a sequência de exibição das categorias
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Setor de Impressão (Opcional)
                      </label>
                      <input
                        type="text"
                        {...register('impressora_destino')}
                        className="input-field text-sm md:text-base"
                        placeholder="Ex: COZINHA, BAR, CHURRASQUEIRA"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Usado para agrupar impressões no aplicativo servidor. Deixe em branco para usar o padrão.
                      </p>
                    </div>

                    <div className="bg-gray-100 dark:bg-gray-800/70 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[{
                        name: 'visivel_delivery',
                        label: 'Delivery',
                        description: 'Disponível na plataforma de entrega'
                      }, {
                        name: 'visivel_garcom',
                        label: 'Garçom',
                        description: 'Visível apenas para o app interno do garçom'
                      }, {
                        name: 'visivel_pdv',
                        label: 'PDV',
                        description: 'Aparece na tela do caixa/local'
                      }, {
                        name: 'perguntar_imprimir',
                        label: 'Perguntar Impressão',
                        description: 'Perguntar ao garçom se deve imprimir'
                      }, {
                        name: 'sempre_imprimir',
                        label: 'Sempre Imprimir',
                        description: 'Ignora a recusa do garçom e força a impressão'
                      }].map((option) => (
                        <label
                          key={option.name}
                          className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-500 transition-colors"
                        >
                          <input
                            {...register(option.name as keyof CategoriaForm)}
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div>
                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 mb-0.5">
                              {option.label}
                            </p>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                              {option.description}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Passo 2: Imagem */}
                {currentStep === 2 && (
                  <div className="space-y-3 md:space-y-4">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Imagem de Fundo (Opcional)
                      </label>

                      {imagePreview && (
                        <div className="relative mb-4">
                          <div className="relative h-32 md:h-40 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-white text-xs md:text-sm font-medium">Preview da imagem de fundo</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={removeImage}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X className="h-3.5 w-3.5 md:h-4 md:w-4" />
                          </button>
                        </div>
                      )}

                      <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-32 md:h-40 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-6 h-6 md:w-8 md:h-8 mb-2 text-gray-500 dark:text-gray-400" />
                            <p className="mb-2 text-xs md:text-sm text-gray-500 dark:text-gray-400 text-center px-2">
                              <span className="font-semibold">Clique para fazer upload</span> ou arraste e solte
                            </p>
                            <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400">PNG, JPG ou GIF (MAX. 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botões de Navegação */}
                <div className="flex flex-col md:flex-row gap-2 md:gap-3 pt-4 md:pt-6 border-t border-gray-200 dark:border-gray-700">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="flex items-center justify-center gap-2 btn-secondary text-sm md:text-base py-2 md:py-2.5 order-2 md:order-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </button>
                  )}
                  <button
                    type="submit"
                    className={`flex items-center justify-center gap-2 btn-primary text-sm md:text-base py-2 md:py-2.5 ${currentStep === 1 ? 'w-full' : 'flex-1'} order-1 md:order-2`}
                  >
                    {currentStep < totalSteps ? (
                      <>
                        Próximo
                        <ChevronRight className="h-4 w-4" />
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        {editingCategoria ? 'Atualizar Categoria' : 'Finalizar e Criar'}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCategoria(null);
                      setSelectedImage(null);
                      setImagePreview(null);
                      setCurrentStep(1);
                      reset();
                    }}
                    className="btn-secondary text-sm md:text-base py-2 md:py-2.5 order-3"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriasPage;
